import { useState } from "react";
import useI18n from "../hooks/useI18n";
import locales from "../locales.json";
import { ensureNotificationPermission, notify } from "../notify";
import { registerSW } from "../sw-register";

export default function CreateAccount({ onCreated }) {
    const { t } = useI18n(locales);
    const [username, setUsername] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [phase, setPhase] = useState("form");
    const [error, setError] = useState("");
    const valid = username.trim().length >= 3 && username.trim().length <= 24;

    const submit = (e) => {
        e?.preventDefault();
        if (!valid || submitting) return;
        setSubmitting(true);
        setPhase("notify");
        setSubmitting(false);
    };

    const allowAndContinue = async () => {
        setSubmitting(true);
        setError("");
        await registerSW();
        const perm = await ensureNotificationPermission();
        if (perm !== "granted") {
            setError(t("notify.need_permission"));
            setSubmitting(false);
            return;
        }
        await notify(t("notify.welcome_title"), { body: t("notify.welcome_body") });
        onCreated({ username: username.trim(), createdAt: Date.now() });
    };

    return (
        <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center p-6">
            <div className="w-full max-w-md bg-white/5 border border-white/10 rounded-2xl p-6 shadow-xl">
                {phase === "form" && (
                    <>
                        <h1 className="text-xl font-semibold">{t("create.title")}</h1>
                        <p className="text-white/70 text-sm mt-1">{t("create.subtitle")}</p>
                        <form onSubmit={submit} className="mt-5 space-y-4">
                            <div>
                                <label className="block text-sm text-white/70 mb-1">{t("settings.username")}</label>
                                <input
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === "Enter") submit(e); }}
                                    className="w-full rounded-xl bg-slate-900 border border-white/10 px-4 py-2 outline-none focus:border-emerald-400"
                                    placeholder={t("settings.username_ph")}
                                    maxLength={24}
                                    autoFocus
                                />
                                <div className="mt-1 text-xs text-white/50">3–24</div>
                            </div>
                            <button
                                type="submit"
                                disabled={!valid || submitting}
                                className={`w-full rounded-xl px-4 py-2 font-medium transition ${valid ? "bg-emerald-500/90 hover:bg-emerald-500" : "bg-emerald-500/40 cursor-not-allowed"}`}
                            >
                                {t("create.continue")}
                            </button>
                        </form>
                    </>
                )}

                {phase === "notify" && (
                    <>
                        <h2 className="text-xl font-semibold">{t("notify.step_title")}</h2>
                        <p className="text-white/70 text-sm mt-1">{t("notify.step_desc")}</p>
                        {error ? <div className="mt-4 text-sm text-rose-400">{error}</div> : null}
                        <div className="mt-6 space-y-3">
                            <button
                                onClick={allowAndContinue}
                                disabled={submitting}
                                className={`w-full rounded-xl px-4 py-2 font-medium transition ${!submitting ? "bg-emerald-500/90 hover:bg-emerald-500" : "bg-emerald-500/40 cursor-not-allowed"}`}
                            >
                                {t("notify.allow")}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
